import { serializeAsJSON } from "@excalidraw/excalidraw/data/json";
import { isInitializedImageElement } from "@excalidraw/element";

import { fnCreateSupabaseClient } from "./supabaseClient";

import type { iDrawingMetadata, iFolderMetadata } from "./types";
import type { ExcalidrawElement, FileId } from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFiles,
} from "@excalidraw/excalidraw/types";

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

const RE_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const fnValidateUUID = (sId: string): boolean => RE_UUID.test(sId);

const fnValidatePath = (sPath: string): boolean => {
  return (
    !sPath.includes("..") &&
    !sPath.includes("//") &&
    !sPath.includes("%2e%2e") &&
    !sPath.includes("%2E%2E")
  );
};

const fnSanitizeName = (sName: string): string => {
  return sName
    .replace(/[<>"']/g, "")
    .trim()
    .slice(0, 100);
};

// ---------------------------------------------------------------------------
// fnSaveDrawing
// ---------------------------------------------------------------------------

interface iSaveResult {
  bSuccess: boolean;
  sDrawingId?: string;
}

export const fnSaveDrawing = async (
  aElements: readonly ExcalidrawElement[],
  oAppState: Partial<AppState>,
  oFiles: BinaryFiles,
  sName: string,
  sToken: string,
  sDrawingId?: string,
  sFolderId?: string | null,
): Promise<iSaveResult> => {
  if (!sToken) {
    return { bSuccess: false };
  }

  if (sDrawingId && !fnValidateUUID(sDrawingId)) {
    return { bSuccess: false };
  }

  const sSanitizedName = fnSanitizeName(sName);
  if (sSanitizedName.length === 0) {
    return { bSuccess: false };
  }

  const oClient = fnCreateSupabaseClient(sToken);

  try {
    // 1. Upsert metadata row FIRST
    const oUpsertData: Record<string, unknown> = {
      name: sSanitizedName,
    };

    if (sDrawingId) {
      oUpsertData.id = sDrawingId;
    }

    if (sFolderId !== undefined) {
      oUpsertData.folder_id = sFolderId;
    }

    const { data: oRow, error: eUpsert } = await oClient
      .from("drawings")
      .upsert(oUpsertData, { onConflict: "id" })
      .select("id, user_id")
      .single();

    if (eUpsert || !oRow) {
      console.error("Cloud save: upsert failed", eUpsert);
      return { bSuccess: false };
    }

    const sId = oRow.id as string;
    const sUserId = oRow.user_id as string;
    const sFilePath = `${sUserId}/${sId}.excalidraw`;

    if (!fnValidatePath(sFilePath)) {
      return { bSuccess: false };
    }

    // 2. Serialize and upload drawing JSON
    const sJson = serializeAsJSON(aElements, oAppState, oFiles, "database");
    const nSizeBytes = new Blob([sJson]).size;

    const { error: eUpload } = await oClient.storage
      .from("drawings")
      .upload(sFilePath, sJson, {
        contentType: "application/json",
        upsert: true,
      });

    if (eUpload) {
      console.error("Cloud save: file upload failed", eUpload);
      return { bSuccess: false };
    }

    // 3. Update size_bytes on metadata row
    await oClient
      .from("drawings")
      .update({ size_bytes: nSizeBytes, file_path: sFilePath })
      .eq("id", sId);

    // 4. Upload images concurrently
    const aImageEntries: [FileId, BinaryFileData][] = [];
    for (const oElement of aElements) {
      if (isInitializedImageElement(oElement) && oFiles[oElement.fileId]) {
        aImageEntries.push([oElement.fileId, oFiles[oElement.fileId]]);
      }
    }

    if (aImageEntries.length > 0) {
      await Promise.all(
        aImageEntries.map(async ([sFileId, oFileData]) => {
          const sImagePath = `${sUserId}/${sId}/images/${sFileId}`;
          if (!fnValidatePath(sImagePath)) {
            return;
          }

          // BinaryFileData.dataURL is a data URL string
          const oBlob = await fetch(oFileData.dataURL).then((r) => r.blob());

          await oClient.storage.from("drawings").upload(sImagePath, oBlob, {
            contentType: oFileData.mimeType,
            upsert: true,
          });
        }),
      );
    }

    return { bSuccess: true, sDrawingId: sId };
  } catch (error) {
    console.error("Cloud save: unexpected error", error);
    return { bSuccess: false };
  }
};

// ---------------------------------------------------------------------------
// fnLoadDrawing
// ---------------------------------------------------------------------------

interface iLoadResult {
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState>;
  files: BinaryFiles;
}

export const fnLoadDrawing = async (
  sDrawingId: string,
  sToken: string,
): Promise<iLoadResult | null> => {
  if (!sToken) {
    return null;
  }

  if (!fnValidateUUID(sDrawingId)) {
    return null;
  }

  const oClient = fnCreateSupabaseClient(sToken);

  try {
    // 1. Fetch metadata row
    const { data: oRow, error: eRow } = await oClient
      .from("drawings")
      .select("*")
      .eq("id", sDrawingId)
      .single();

    if (eRow || !oRow) {
      console.error("Cloud load: row fetch failed", eRow);
      return null;
    }

    const sFilePath = oRow.file_path as string;
    if (!fnValidatePath(sFilePath)) {
      return null;
    }

    // 2. Download JSON
    const { data: oBlob, error: eDownload } = await oClient.storage
      .from("drawings")
      .download(sFilePath);

    if (eDownload || !oBlob) {
      console.error("Cloud load: file download failed", eDownload);
      return null;
    }

    const sJson = await oBlob.text();

    let oParsed: any;
    try {
      oParsed = JSON.parse(sJson);
    } catch {
      console.error("Cloud load: corrupted JSON");
      return null;
    }

    const aElements = oParsed.elements || [];
    const oAppState = oParsed.appState || {};
    const oFiles: BinaryFiles = {};

    // 3. Find image references and download concurrently
    const sUserId = oRow.user_id as string;
    const aImageIds: FileId[] = [];
    for (const oElement of aElements) {
      if (isInitializedImageElement(oElement)) {
        aImageIds.push(oElement.fileId);
      }
    }

    if (aImageIds.length > 0) {
      const aImageResults = await Promise.all(
        aImageIds.map(async (sFileId) => {
          const sImagePath = `${sUserId}/${sDrawingId}/images/${sFileId}`;
          if (!fnValidatePath(sImagePath)) {
            return null;
          }

          const { data: oImageBlob, error: eImage } = await oClient.storage
            .from("drawings")
            .download(sImagePath);

          if (eImage || !oImageBlob) {
            // Skip missing images gracefully
            return null;
          }

          // Convert to data URL
          const oArrayBuffer = await oImageBlob.arrayBuffer();
          const sMimeType = oImageBlob.type || "image/png";
          const sBase64 = btoa(
            new Uint8Array(oArrayBuffer).reduce(
              (sData, nByte) => sData + String.fromCharCode(nByte),
              "",
            ),
          );
          const sDataUrl = `data:${sMimeType};base64,${sBase64}`;

          return {
            sFileId,
            oFileData: {
              mimeType: sMimeType,
              id: sFileId,
              dataURL: sDataUrl,
              created: Date.now(),
              lastRetrieved: Date.now(),
            } as BinaryFileData,
          };
        }),
      );

      for (const oResult of aImageResults) {
        if (oResult) {
          oFiles[oResult.sFileId] = oResult.oFileData;
        }
      }
    }

    // Also restore embedded file data from the JSON if present
    if (oParsed.files) {
      for (const [sKey, oValue] of Object.entries(oParsed.files)) {
        if (!oFiles[sKey as FileId]) {
          oFiles[sKey as FileId] = oValue as BinaryFileData;
        }
      }
    }

    return { elements: aElements, appState: oAppState, files: oFiles };
  } catch (error) {
    console.error("Cloud load: unexpected error", error);
    return null;
  }
};

// ---------------------------------------------------------------------------
// fnListDrawings
// ---------------------------------------------------------------------------

export const fnListDrawings = async (
  sToken: string,
  sFolderId?: string | null,
): Promise<iDrawingMetadata[]> => {
  if (!sToken) {
    return [];
  }

  if (
    sFolderId !== undefined &&
    sFolderId !== null &&
    !fnValidateUUID(sFolderId)
  ) {
    return [];
  }

  const oClient = fnCreateSupabaseClient(sToken);

  let oQuery = oClient
    .from("drawings")
    .select("*")
    .order("updated_at", { ascending: false })
    .limit(100);

  if (sFolderId === null) {
    oQuery = oQuery.is("folder_id", null);
  } else if (sFolderId !== undefined) {
    oQuery = oQuery.eq("folder_id", sFolderId);
  }

  const { data, error } = await oQuery;

  if (error) {
    console.error("Cloud list: query failed", error);
    return [];
  }

  return (data || []) as iDrawingMetadata[];
};

// ---------------------------------------------------------------------------
// fnDeleteDrawing
// ---------------------------------------------------------------------------

export const fnDeleteDrawing = async (
  sDrawingId: string,
  sToken: string,
): Promise<boolean> => {
  if (!sToken || !fnValidateUUID(sDrawingId)) {
    return false;
  }

  const oClient = fnCreateSupabaseClient(sToken);

  try {
    // 1. Fetch row first to get user_id for file paths
    const { data: oRow, error: eRow } = await oClient
      .from("drawings")
      .select("user_id")
      .eq("id", sDrawingId)
      .single();

    if (eRow || !oRow) {
      return false;
    }

    const sUserId = oRow.user_id as string;

    // 2. Delete row FIRST (orphaned files > row pointing to deleted files)
    const { error: eDelete } = await oClient
      .from("drawings")
      .delete()
      .eq("id", sDrawingId);

    if (eDelete) {
      console.error("Cloud delete: row delete failed", eDelete);
      return false;
    }

    // 3. Delete JSON file from storage
    const sFilePath = `${sUserId}/${sDrawingId}.excalidraw`;
    await oClient.storage.from("drawings").remove([sFilePath]);

    // 4. List and bulk-delete images
    const sImagePrefix = `${sUserId}/${sDrawingId}/images`;
    const { data: aFiles } = await oClient.storage
      .from("drawings")
      .list(sImagePrefix, { limit: 1000 });

    if (aFiles && aFiles.length > 0) {
      const aImagePaths = aFiles.map((f) => `${sImagePrefix}/${f.name}`);
      await oClient.storage.from("drawings").remove(aImagePaths);
    }

    return true;
  } catch (error) {
    console.error("Cloud delete: unexpected error", error);
    return false;
  }
};

// ---------------------------------------------------------------------------
// fnRenameDrawing
// ---------------------------------------------------------------------------

export const fnRenameDrawing = async (
  sDrawingId: string,
  sNewName: string,
  sToken: string,
): Promise<boolean> => {
  if (!sToken || !fnValidateUUID(sDrawingId)) {
    return false;
  }

  const sSanitized = fnSanitizeName(sNewName);
  if (sSanitized.length === 0) {
    return false;
  }

  const oClient = fnCreateSupabaseClient(sToken);

  const { error } = await oClient
    .from("drawings")
    .update({ name: sSanitized })
    .eq("id", sDrawingId);

  if (error) {
    console.error("Cloud rename: update failed", error);
    return false;
  }

  return true;
};

// ---------------------------------------------------------------------------
// Folder CRUD
// ---------------------------------------------------------------------------

interface iListFoldersResult {
  aFolders: iFolderMetadata[];
  bError: boolean;
}

export const fnListFolders = async (
  sToken: string,
): Promise<iListFoldersResult> => {
  if (!sToken) {
    return { aFolders: [], bError: true };
  }

  const oClient = fnCreateSupabaseClient(sToken);

  const { data, error } = await oClient
    .from("folders")
    .select("id, name, created_at")
    .order("name")
    .limit(200);

  if (error) {
    console.error("Cloud folders: list failed", error);
    return { aFolders: [], bError: true };
  }

  return { aFolders: (data || []) as iFolderMetadata[], bError: false };
};

interface iCreateFolderResult {
  bSuccess: boolean;
  sError?: string;
  sFolderId?: string;
}

export const fnCreateFolder = async (
  sName: string,
  sToken: string,
): Promise<iCreateFolderResult> => {
  if (!sToken) {
    return { bSuccess: false, sError: "no token" };
  }

  const sSanitized = fnSanitizeName(sName);
  if (sSanitized.length === 0) {
    return { bSuccess: false, sError: "empty name" };
  }

  const oClient = fnCreateSupabaseClient(sToken);

  const { data, error } = await oClient
    .from("folders")
    .insert({ name: sSanitized })
    .select("id")
    .single();

  if (error) {
    if (error.code === "23505") {
      return { bSuccess: false, sError: "duplicate" };
    }
    console.error("Cloud folders: create failed", error);
    return { bSuccess: false, sError: error.message };
  }

  return { bSuccess: true, sFolderId: data?.id as string };
};

interface iDeleteFolderResult {
  bSuccess: boolean;
  sError?: string;
}

export const fnDeleteFolder = async (
  sFolderId: string,
  sToken: string,
): Promise<iDeleteFolderResult> => {
  if (!sToken || !fnValidateUUID(sFolderId)) {
    return { bSuccess: false, sError: "invalid input" };
  }

  const oClient = fnCreateSupabaseClient(sToken);

  const { error } = await oClient.from("folders").delete().eq("id", sFolderId);

  if (error) {
    console.error("Cloud folders: delete failed", error);
    return { bSuccess: false, sError: error.message };
  }

  return { bSuccess: true };
};

interface iRenameFolderResult {
  bSuccess: boolean;
  sError?: string;
}

export const fnRenameFolder = async (
  sFolderId: string,
  sNewName: string,
  sToken: string,
): Promise<iRenameFolderResult> => {
  if (!sToken || !fnValidateUUID(sFolderId)) {
    return { bSuccess: false, sError: "invalid input" };
  }

  const sSanitized = fnSanitizeName(sNewName);
  if (sSanitized.length === 0) {
    return { bSuccess: false, sError: "empty name" };
  }

  const oClient = fnCreateSupabaseClient(sToken);

  const { error } = await oClient
    .from("folders")
    .update({ name: sSanitized })
    .eq("id", sFolderId);

  if (error) {
    if (error.code === "23505") {
      return { bSuccess: false, sError: "duplicate" };
    }
    console.error("Cloud folders: rename failed", error);
    return { bSuccess: false, sError: error.message };
  }

  return { bSuccess: true };
};

// ---------------------------------------------------------------------------
// fnMoveDrawing
// ---------------------------------------------------------------------------

export const fnMoveDrawing = async (
  sDrawingId: string,
  sFolderId: string | null,
  sToken: string,
): Promise<boolean> => {
  if (!sToken || !fnValidateUUID(sDrawingId)) {
    return false;
  }

  if (sFolderId !== null && !fnValidateUUID(sFolderId)) {
    return false;
  }

  const oClient = fnCreateSupabaseClient(sToken);

  const { error } = await oClient
    .from("drawings")
    .update({ folder_id: sFolderId })
    .eq("id", sDrawingId);

  if (error) {
    console.error("Cloud move: update failed", error);
    return false;
  }

  return true;
};
